import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {AsyncCombobox} from '@common-web/components/ui/combobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import type {CatalogProduct} from '@client/hooks/useProductSelection';

type GroupOption = {groupId: string; name: string};

type GroupCoverageStepProps = {
  urlGroupId: string | undefined;
  selectedGroupId: string;
  selectedGroupName: string;
  onGroupChange: (groupId: string, name: string) => void;
  groupOptions: GroupOption[];
  onGroupSearch: (search: string) => void;
  isLoadingGroups: boolean;
  coverageEffectiveDate: string;
  onCoverageEffectiveDateChange: (value: string) => void;
  // Product selection at creation time is Champ-only; other tenants assign
  // products through the analyzer flow after the member exists in VBA.
  showProductSelection: boolean;
  initialProductId: string;
  onInitialProductChange: (value: string) => void;
  productCatalog: CatalogProduct[];
  isLoadingProducts: boolean;
  canProceed: boolean;
  onBack: () => void;
  onContinue: () => void;
};

export function GroupCoverageStep({
  urlGroupId,
  selectedGroupId,
  selectedGroupName,
  onGroupChange,
  groupOptions,
  onGroupSearch,
  isLoadingGroups,
  coverageEffectiveDate,
  onCoverageEffectiveDateChange,
  showProductSelection,
  initialProductId,
  onInitialProductChange,
  productCatalog,
  isLoadingProducts,
  canProceed,
  onBack,
  onContinue,
}: GroupCoverageStepProps) {
  return (
    <>
      <Card className='border-0 shadow-lg bg-white overflow-hidden mb-6'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Step 2: Group & Coverage
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6 space-y-4'>
          {urlGroupId ? (
            <div className='flex items-center gap-2 p-3 rounded-lg bg-gray-50'>
              <span className='text-sm font-medium text-gray-500'>Group:</span>
              <span className='text-sm font-semibold text-brand-navy'>{urlGroupId}</span>
            </div>
          ) : (
            <div className='space-y-2'>
              <Label htmlFor='groupId'>Group *</Label>
              <AsyncCombobox
                value={selectedGroupId}
                selectedLabel={selectedGroupName}
                setValue={onGroupChange}
                onSearch={onGroupSearch}
                items={groupOptions.map((g) => ({label: g.name, value: g.groupId}))}
                isLoading={isLoadingGroups}
                message='Select a group...'
                placeholder='Search groups by name...'
                emptyMessage='No groups found.'
                allowClear
              />
            </div>
          )}
          <div className='space-y-2'>
            <Label htmlFor='coverageEffectiveDate'>Coverage Effective Date *</Label>
            <Input
              id='coverageEffectiveDate'
              type='date'
              value={coverageEffectiveDate}
              onChange={(e) => onCoverageEffectiveDateChange(e.target.value)}
            />
          </div>
          {showProductSelection && (
            <div className='space-y-2'>
              <Label htmlFor='initialProduct'>Initial Product *</Label>
              <Select value={initialProductId} onValueChange={onInitialProductChange}>
                <SelectTrigger id='initialProduct'>
                  <SelectValue placeholder='Select product...' />
                </SelectTrigger>
                <SelectContent>
                  {productCatalog.map((p: CatalogProduct) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {p.productId ? ` (${p.productId})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isLoadingProducts && <p className='text-xs text-gray-400'>Loading products…</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <div className='flex items-center justify-between'>
        <Button variant='outline' onClick={onBack}>
          Back
        </Button>
        <Button onClick={onContinue} disabled={!canProceed}>
          Continue to Step 3
        </Button>
      </div>
    </>
  );
}
